import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAgencyBusiness } from '../services/api';
import { useAgency } from '../context/AgencyContext';
import { ArrowLeftIcon, LanguageIcon } from '@heroicons/react/24/outline';

// Language types and constants (local copy to avoid ESM/CJS issues with shared package)
type SupportedLanguage = 'en' | 'es';

interface LanguageConfig {
  code: string;
  name: string;
  nativeName: string;
}

const SUPPORTED_LANGUAGES: Record<SupportedLanguage, LanguageConfig> = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
  },
};

// Greeting templates by language
const GREETING_TEMPLATES: Record<SupportedLanguage, (name: string) => string> = {
  en: (name) => `Hello, thank you for calling ${name}. How may I help you today?`,
  es: (name) => `Hola, gracias por llamar a ${name}. ¿En qué puedo ayudarle hoy?`,
};

export default function AddClientPage() {
  const navigate = useNavigate();
  const { currentAgency, refreshBusinesses } = useAgency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get default language from agency localization
  const defaultLanguage: SupportedLanguage = (currentAgency?.localization?.language as SupportedLanguage) || 'en';

  const [formData, setFormData] = useState({
    name: '',
    contactFirstName: '',
    contactLastName: '',
    timezone: 'America/New_York',
    ownerEmail: '',
    language: defaultLanguage,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentAgency) {
      setError('No agency selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Generate language-appropriate greeting
      const greetingTemplate = GREETING_TEMPLATES[formData.language as SupportedLanguage] || GREETING_TEMPLATES.en;
      const greeting = greetingTemplate(formData.name);

      const business = await createAgencyBusiness(currentAgency.id, {
        name: formData.name,
        contactFirstName: formData.contactFirstName || undefined,
        contactLastName: formData.contactLastName || undefined,
        phoneNumber: '',
        timezone: formData.timezone,
        greeting,
        ownerEmail: formData.ownerEmail || undefined,
        businessHours: {
          monday: { open: '09:00', close: '17:00', closed: false },
          tuesday: { open: '09:00', close: '17:00', closed: false },
          wednesday: { open: '09:00', close: '17:00', closed: false },
          thursday: { open: '09:00', close: '17:00', closed: false },
          friday: { open: '09:00', close: '17:00', closed: false },
          saturday: { open: '09:00', close: '17:00', closed: true },
          sunday: { open: '09:00', close: '17:00', closed: true },
        },
        services: [],
        // Localization settings
        localization: {
          language: formData.language,
        },
      } as any);

      await refreshBusinesses();
      // Navigate to client detail page - voice tab to set up agent
      navigate(`/clients/${business.id}?tab=voice`);
    } catch (err: any) {
      console.error('Error creating business:', err);
      setError(err.message || 'Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select an agency first.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/clients')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Clients
        </button>
      </div>

      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Add New Client</h1>
        <p className="text-gray-600 mt-2">
          Enter the basic information for your new client. You can configure the AI voice agent after creation.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {/* Business Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Acme Dental Clinic"
          />
        </div>

        {/* Contact Name */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Contact First Name
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={formData.contactFirstName}
              onChange={(e) => setFormData({ ...formData, contactFirstName: e.target.value })}
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Contact Last Name
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={formData.contactLastName}
              onChange={(e) => setFormData({ ...formData, contactLastName: e.target.value })}
              placeholder="Smith"
            />
          </div>
        </div>

        {/* Owner Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Client Email
            <span className="text-gray-400 font-normal ml-1">(optional - will receive invitation)</span>
          </label>
          <input
            type="email"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={formData.ownerEmail}
            onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
            placeholder="client@example.com"
          />
          <p className="mt-1 text-xs text-gray-500">
            An invitation email will be sent to set up their account and access the client portal
          </p>
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Timezone</label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          >
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
            <option value="America/Anchorage">Alaska Time</option>
            <option value="Pacific/Honolulu">Hawaii Time</option>
          </select>
        </div>

        {/* Language */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            <span className="inline-flex items-center gap-1">
              <LanguageIcon className="h-4 w-4" />
              AI Language
            </span>
          </label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value as SupportedLanguage })}
          >
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
              <option key={code} value={code}>
                {lang.name} ({lang.nativeName})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            The AI receptionist will speak in this language
          </p>
        </div>

        {/* Submit */}
        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading || !formData.name}
            className="w-full inline-flex justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Client'}
          </button>
          <p className="mt-2 text-xs text-gray-500 text-center">
            After creation, you can configure the AI voice agent in the Voice tab
          </p>
        </div>
      </form>
    </div>
  );
}
